import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePageContent } from "@/i18n/pageContent";

const ADOPTION = {
  it: [
    { year: "1999 / 2002", text: "Austria, Belgio, Finlandia, Francia, Germania, Irlanda, Italia, Lussemburgo, Paesi Bassi, Portogallo, Spagna" },
    { year: "2007", text: "Slovenia" },
    { year: "2008", text: "Cipro · Malta" },
    { year: "2009", text: "Slovacchia" },
    { year: "2011", text: "Estonia" },
    { year: "2014", text: "Lettonia" },
    { year: "2015", text: "Lituania" },
    { year: "2023", text: "Croazia" },
    { year: "2026", text: "Bulgaria — l'ultima adesione" },
  ],
  en: [
    { year: "1999 / 2002", text: "Austria, Belgium, Finland, France, Germany, Ireland, Italy, Luxembourg, Netherlands, Portugal, Spain" },
    { year: "2007", text: "Slovenia" },
    { year: "2008", text: "Cyprus · Malta" },
    { year: "2009", text: "Slovakia" },
    { year: "2011", text: "Estonia" },
    { year: "2014", text: "Latvia" },
    { year: "2015", text: "Lithuania" },
    { year: "2023", text: "Croatia" },
    { year: "2026", text: "Bulgaria — the latest to join" },
  ],
};

const TXT = {
  it: {
    p1Pre: "L'introduzione dell'euro è avvenuta in più fasi. I primi paesi ad adottarlo nel 1999 per i pagamenti elettronici e nel 2002 in forma contante furono undici. Da allora il club è cresciuto fino agli attuali ",
    p1Strong: "21 Stati membri",
    p1Post: ".",
    p2: "Adottare l'euro non è semplicemente un cambio di valuta: comporta l'allineamento con le politiche economiche e fiscali dell'UE, con l'obiettivo di garantire stabilità dei prezzi, finanze pubbliche sostenibili e integrazione dei mercati.",
    timelineEyebrow: "— Adoption Timeline",
    timelineTitleA: "Le tappe",
    timelineTitleB: "dell'euro",
    yearLabel: "Anno",
    beyondTitle: "Oltre l'Unione",
    p3: "Al di fuori dell'UE, l'euro è utilizzato anche da Andorra, Monaco, San Marino e Città del Vaticano sulla base di accordi monetari specifici. Altri territori, come Kosovo e Montenegro, lo usano de facto pur non facendo parte formalmente dell'area euro.",
    p4: "L'euro, dunque, non è solo un mezzo di pagamento, ma un simbolo della cooperazione europea e del processo di integrazione che ha portato l'Unione a un livello senza precedenti nella storia del continente.",
    cta: "Europe's Languages",
  },
  en: {
    p1Pre: "The introduction of the euro took place in several stages. The first countries to adopt it in 1999 for electronic payments and in 2002 in cash form were eleven. Since then the club has grown to the current ",
    p1Strong: "21 Member States",
    p1Post: ".",
    p2: "Adopting the euro is not simply a change of currency: it entails alignment with EU economic and fiscal policies, with the objective of ensuring price stability, sustainable public finances and market integration.",
    timelineEyebrow: "— Adoption Timeline",
    timelineTitleA: "The stages of",
    timelineTitleB: "the euro",
    yearLabel: "Year",
    beyondTitle: "Beyond the Union",
    p3: "Outside the EU, the euro is also used by Andorra, Monaco, San Marino and Vatican City on the basis of specific monetary agreements. Other territories, such as Kosovo and Montenegro, use it de facto despite not formally being part of the euro area.",
    p4: "The euro, therefore, is not just a means of payment but a symbol of European cooperation and of the integration process that has brought the Union to an unprecedented level in the continent's history.",
    cta: "Europe's Languages",
  },
};

export default function EUEuro() {
  const { i18n } = useTranslation();
  const c = usePageContent("euEuro");
  const lang = i18n.language === "en" ? "en" : "it";
  const txt = TXT[lang];
  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
        image="/eu-euro-hero.jpg"
      />
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-6 text-[#1e293b] leading-[1.9] text-base md:text-[17px]">
          <p className="reveal">{txt.p1Pre}<strong className="text-[#1a365d]">{txt.p1Strong}</strong>{txt.p1Post}</p>
          <p className="reveal">{txt.p2}</p>
        </div>
      </section>
      <section className="bg-[#fafaf7] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="mb-10 reveal">
            <span className="eyebrow">{txt.timelineEyebrow}</span>
            <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-tight">
              {txt.timelineTitleA} <span className="italic">{txt.timelineTitleB}</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {ADOPTION[lang].map((a, i) => (
              <div key={a.year} className={`bg-white border-l-2 border-[#d4af37] p-6 md:p-7 reveal delay-${(i % 4) + 1}`}>
                <div className="text-[11px] tracking-[0.32em] uppercase text-[#475569] font-body">{txt.yearLabel}</div>
                <div className="font-display text-3xl md:text-4xl text-[#1a365d] mt-1.5 leading-tight">{a.year}</div>
                <p className="mt-4 text-[#1e293b] text-[14.5px] leading-[1.8]">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-6 text-[#1e293b] leading-[1.9] text-base md:text-[17px] reveal">
          <h3 className="font-display text-2xl md:text-3xl text-[#1a365d] leading-tight">{txt.beyondTitle}</h3>
          <p>{txt.p3}</p>
          <p>{txt.p4}</p>
          <div className="pt-6">
            <Link to="/eu-languages" className="btn-outline">
              {txt.cta} <ArrowRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
